import {ElementPosition} from '@dvrd/dvr-controls';

import {Icon} from '@iconify/react';

import React, {useCallback, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {PortalSelectOption, SearchRankingMode} from '../../../util/types';

import BolmateButton from '../../controls/button/bolmateButton';

import BolmateSegmentedToggle from '../../controls/button/bolmateSegmentedToggle';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import BolmateSearchInput from '../../controls/search/bolmateSearchInput';

interface Props {
    onSearchChange: (value: string) => void;
    rankingMode: SearchRankingMode;
    onRankingModeChange: (mode: SearchRankingMode) => void;
    selectedCountries: Array<'NL' | 'BE'>;
    onCountryChange: (value: string) => void;
    selectedAccountIDs: Array<string>;
    storeItems: Array<PortalSelectOption>;
    onStoreChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    onExportExcel: VoidFunction;
    visibleColumnFields: Array<string>;
    allColumnItems: Array<PortalSelectOption>;
    onColumnsChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    onImportClick: VoidFunction;
    manualKeywordsCount: number;
    manualKeywordsLimit: number | null;
    onCloseAccountItems: VoidFunction;
    accountID: string;
}

const flag = (code: string) => <span className={`fi fi-${code}`} style={{margin: '0 0.375rem'}} />;
const countryItems: Array<PortalSelectOption> = [{value: 'NL', label: <span>{flag('nl')} NL</span>}];

export default function SearchRankingsControlBar(props: Props) {
    const {t} = useTranslation();
    const RANKING_MODE_OPTIONS = useMemo(
        () => [
            {value: 'best' as SearchRankingMode, label: t('searchRankings.controlBar.best', 'Beste')},
            {value: 'average' as SearchRankingMode, label: t('searchRankings.controlBar.avg', 'Gem.')}
        ],
        [t]
    );
    const {
        onSearchChange,
        rankingMode,
        onRankingModeChange,
        selectedCountries,
        onCountryChange,
        selectedAccountIDs,
        storeItems,
        onStoreChange,
        visibleColumnFields,
        allColumnItems,
        onColumnsChange,
        onImportClick,
        manualKeywordsCount,
        manualKeywordsLimit,
        onCloseAccountItems,
        accountID,
        onExportExcel
    } = props;
    const importDisabled = useMemo(() => {
        if (manualKeywordsLimit === null) return false;
        return manualKeywordsCount >= manualKeywordsLimit;
    }, [manualKeywordsCount, manualKeywordsLimit]);

    const handleCountryChange = useCallback(
        (value: string | null) => {
            onCountryChange(value ?? 'NL');
        },
        [onCountryChange]
    );

    return (
        <div className='search-rankings-control-bar'>
            {/* Left: Search */}
            <div className='control-bar-left'>
                <BolmateSearchInput
                    onChange={onSearchChange}
                    placeholder={t('searchRankings.controlBar.searchProducts', 'Zoek producten...')}
                    debounce={500}
                    size='small'
                />
            </div>

            {/* Right: Toggle + Country + Stores + Columns + Period + Import + Export */}
            <div className='control-bar-right'>
                {/* Ranking Mode Toggle */}
                <BolmateSegmentedToggle
                    options={RANKING_MODE_OPTIONS}
                    value={rankingMode}
                    onChange={onRankingModeChange}
                />

                {/* Country Selector */}
                <PortalSelectController
                    onChange={handleCountryChange}
                    value={selectedCountries.join(',')}
                    items={countryItems}
                    label={t('searchRankings.controlBar.country', 'Land')}
                    size='small'
                    shape='rounded'
                />

                {/* Store Selector */}
                {storeItems.length > 0 && (
                    <PortalSelectController
                        key={storeItems.map(s => s.value).join(',')}
                        onChange={onStoreChange}
                        onCloseItems={onCloseAccountItems}
                        defaultValue={selectedAccountIDs.length > 0 ? selectedAccountIDs : [accountID]}
                        items={storeItems}
                        label={t('searchRankings.controlBar.stores', 'Winkels')}
                        placeholder={t('searchRankings.controlBar.noStores', 'Niets geselecteerd')}
                        size='small'
                        shape='rounded'
                        multiple
                        controls={['selectAll', 'deselectAll']}
                        parseSelectValue={(values: Array<string>) => {
                            if (values.length === 0)
                                return t('searchRankings.controlBar.noStores', 'Niets geselecteerd');
                            else if (values.length === storeItems.length)
                                return t('searchRankings.controlBar.allStores', 'Alle winkels');
                            if (values.length === 1) return t('searchRankings.controlBar.oneStore', '1 winkel');
                            return t('searchRankings.controlBar.storesCount', '{{count}} winkels', {
                                count: values.length
                            });
                        }}
                    />
                )}

                {/* Column Selector */}
                <PortalSelectController
                    onChange={onColumnsChange}
                    value={visibleColumnFields}
                    items={allColumnItems}
                    label={t('searchRankings.controlBar.columns', 'Kolommen')}
                    placeholder={t('searchRankings.controlBar.columns', 'Kolommen')}
                    size='small'
                    shape='rounded'
                    multiple
                    controls={['selectAll', 'deselectAll']}
                    itemsHeight='30rem'
                    disableAutoScroll
                    parseSelectValue={(values: Array<string>) => {
                        if (values.length === 1) return t('searchRankings.controlBar.oneColumn', '1 kolom');
                        return t('searchRankings.controlBar.columnsCount', '{{count}} kolommen', {
                            count: values.length
                        });
                    }}
                />

                {/* Import CSV */}
                <BolmateButton
                    onClick={onImportClick}
                    label={t('searchRankings.controlBar.import', 'KeywordsImport')}
                    tertiary
                    color='blue'
                    size='small'
                    shape='rounded'
                    ornaments={{
                        element: <Icon icon='mdi:file-upload-outline' style={{fontSize: '1rem'}} />,
                        placement: ElementPosition.LEFT
                    }}
                    disabled={importDisabled}
                />

                {/* Export Excel */}
                <span>
                    <BolmateButton
                        onClick={onExportExcel}
                        label={t('searchRankings.controlBar.export', 'Export')}
                        tertiary
                        color='green'
                        size='small'
                        shape='rounded'
                        ornaments={{
                            element: <Icon icon='mdi:file-excel-outline' style={{fontSize: '1rem'}} />,
                            placement: ElementPosition.LEFT
                        }}
                    />
                </span>
            </div>
        </div>
    );
}
